import type { Claim } from '../api/claims';
import { isBulkSubmitted } from '../utils/bulkSubmission';

type LocActionClaim = Pick<
  Claim,
  | 'id' | 'isLegacyClaim' | 'locDocUrl' | 'locPreviousDocUrl' | 'locSentDate' | 'locPreviousSentDate' | 'status'
  | 'submissionSent' | 'submissionSentDate'
>;

export interface LocActionAvailability {
  action: 'send' | 'resend';
  label: 'Send LOC' | 'Send new LOC' | 'Resend LOC';
  disabled: boolean;
  /** True when the button must not be rendered at all (bulk-submitted claim). */
  hidden: boolean;
  tooltip: string;
}

export const getLocActionAvailability = (
  claim: LocActionClaim,
  busy = false,
): LocActionAvailability => {
  const isFcClaim = Boolean(
    claim.isLegacyClaim && String(claim.id || '').toUpperCase().startsWith('FC'),
  );
  const hasStoredDocument = Boolean(claim.locDocUrl || claim.locPreviousDocUrl);
  const hasPreviousLoc = isFcClaim
    ? hasStoredDocument
    : Boolean(claim.locSentDate || claim.locPreviousSentDate || hasStoredDocument);
  const action = hasPreviousLoc ? 'resend' : 'send';
  const label = action === 'resend'
    ? 'Resend LOC'
    : isFcClaim ? 'Send new LOC' : 'Send LOC';

  if (isBulkSubmitted(claim)) {
    return {
      action,
      label,
      disabled: true,
      hidden: true,
      tooltip: 'This claim was sent to the lender in a bulk submission, so an LOC can\'t be sent.',
    };
  }

  if (claim.isLegacyClaim && !isFcClaim) {
    return {
      action,
      label,
      disabled: true,
      hidden: false,
      tooltip: 'LOC sending is unavailable for this legacy claim type.',
    };
  }

  if (claim.status === 'CANCELLED') {
    return {
      action,
      label,
      disabled: true,
      hidden: false,
      tooltip: `This claim is cancelled. Reinstate it before ${action === 'resend' ? 'resending' : 'sending'} an LOC.`,
    };
  }

  if (busy) {
    return {
      action,
      label,
      disabled: true,
      hidden: false,
      tooltip: 'Wait for the current claim action to finish before sending an LOC.',
    };
  }

  if (isFcClaim && !hasStoredDocument) {
    return {
      action: 'send',
      label: 'Send new LOC',
      disabled: false,
      hidden: false,
      tooltip: 'No previous LOC document is stored for this FC claim, so a new LOC will be created and sent.',
    };
  }

  return {
    action,
    label,
    disabled: false,
    hidden: false,
    tooltip: action === 'resend'
      ? 'Review the current claim details, preview a new LOC and resend it to the lender.'
      : 'Create and send a new LOC to the lender.',
  };
};
